/**
 * Visual + label constants shared across the Epstein Files V1 module.
 */

// Short kind chip label, matches the small top-right corner badge on the
// public Evidence Board case cards (PERSON / ORG / DOC / PROPERTY / FLIGHT
// / COMMS / COURT / EVIDENCE). Used by EvidenceBoard.jsx.
export const KIND_CHIP = {
  person: "PERSON",
  organization: "ORG",
  government_office: "ORG",
  company: "ORG",
  financial_entity: "FINANCIAL",
  shell_company: "FINANCIAL",
  trust: "FINANCIAL",
  account: "FINANCIAL",
  financial: "FINANCIAL",
  property: "PROPERTY",
  location: "PROPERTY",
  aircraft: "FLIGHT",
  flight: "FLIGHT",
  flight_event: "FLIGHT",
  vessel: "FLIGHT",
  email: "COMMS",
  communications: "COMMS",
  document: "DOC",
  photo: "EVIDENCE",
  evidence: "EVIDENCE",
  filing: "COURT",
  legal_record: "COURT",
  government_record: "DOC",
  event: "EVENT",
  timeline_event: "EVENT",
  reference: "REF",
  phone_number: "REF",
  alias: "ALIAS",
};

// Entity kinds that should render a LARGE centered amber icon when they have
// no photo on file — matches the icon-card style in the reference screenshot
// (envelope for emails, scales for court, plane for flights, folder for
// documents). Kinds NOT in this set fall back to the small placeholder glyph.
export const ICON_CARD_KINDS = new Set([
  "aircraft", "flight", "flight_event", "vessel",
  "email", "communications",
  "document",
  "filing", "legal_record",
  "financial_entity", "shell_company", "trust", "account", "financial",
  "phone_number", "event", "timeline_event",
  "photo", "evidence",
]);

export const KIND_META = {
  person:           { label: "PERSON",       color: "#E4DFD3", icon: "◉" },
  organization:     { label: "ORGANIZATION", color: "#CBB892", icon: "▣" },
  location:         { label: "LOCATION",     color: "#9FA8D4", icon: "◈" },
  document:         { label: "DOCUMENT",     color: "#5DD4B3", icon: "▤" },
  event:            { label: "EVENT",        color: "#E0A526", icon: "◆" },
  flight:           { label: "FLIGHT",       color: "#9FA8D4", icon: "✈" },
  filing:           { label: "FILING",       color: "#D92D20", icon: "§" },
  photo:            { label: "PHOTO",        color: "#CBB892", icon: "□" },
  reference:        { label: "REFERENCE",    color: "#A39E93", icon: "›" },
  financial:        { label: "FINANCIAL",    color: "#E0A526", icon: "¤" },
  // Phase 2 expansion — distinct visual identity per kind.
  property:         { label: "PROPERTY",        color: "#5DD4B3", icon: "▦" },
  financial_entity: { label: "SHELL / FINANCIAL", color: "#E0A526", icon: "$" },
  shell_company:    { label: "SHELL COMPANY",   color: "#E0A526", icon: "$" },
  trust:            { label: "TRUST",           color: "#E0A526", icon: "₮" },
  company:          { label: "COMPANY",         color: "#CBB892", icon: "▣" },
  aircraft:         { label: "AIRCRAFT",        color: "#9FA8D4", icon: "✈" },
  flight_event:     { label: "FLIGHT EVENT",    color: "#9FA8D4", icon: "→" },
  legal_record:     { label: "LEGAL / COURT",   color: "#D92D20", icon: "§" },
  timeline_event:   { label: "TIMELINE",        color: "#E0A526", icon: "◇" },
  government_record:{ label: "GOV RECORD",      color: "#5DD4B3", icon: "✦" },
  email:            { label: "EMAIL",           color: "#9FA8D4", icon: "✉" },
  vessel:           { label: "VESSEL",          color: "#9FA8D4", icon: "⚓" },
  account:          { label: "ACCOUNT",         color: "#E0A526", icon: "#" },
  phone_number:     { label: "PHONE",           color: "#A39E93", icon: "☎" },
  alias:            { label: "ALIAS",           color: "#A39E93", icon: "›" },
};

export const REL_META = {
  photographed_with:                 { label: "PHOTOGRAPHED WITH",         color: "#CBB892" },
  appears_in_same_flight_log:        { label: "SHARED FLIGHT LOG",         color: "#9FA8D4" },
  listed_in_flight_logs:             { label: "LISTED IN FLIGHT LOGS",     color: "#9FA8D4" },
  listed_in_black_book:              { label: "LISTED IN BLACK BOOK",      color: "#A39E93" },
  named_in_court_filing:             { label: "NAMED IN COURT FILING",     color: "#D92D20" },
  named_in_deposition:               { label: "NAMED IN DEPOSITION",       color: "#D92D20" },
  witness_claim_association:         { label: "WITNESS CLAIM",             color: "#E0A526" },
  business_association:              { label: "BUSINESS ASSOCIATION",      color: "#E0A526" },
  property_association:              { label: "PROPERTY ASSOCIATION",      color: "#5DD4B3" },
  event_attendance:                  { label: "EVENT ATTENDANCE",          color: "#CBB892" },
  employment_or_staff_link:          { label: "EMPLOYMENT / STAFF",        color: "#9FA8D4" },
  travel_association:                { label: "TRAVEL ASSOCIATION",        color: "#9FA8D4" },
  communication_or_contact_reference:{ label: "CONTACT REFERENCE",         color: "#A39E93" },
  financial_link:                    { label: "FINANCIAL LINK",            color: "#E0A526" },
  other_editor_defined:              { label: "OTHER",                     color: "#A39E93" },
  // Phase 2 expansion — mixed-kind relationship labels.
  associated_with:                   { label: "ASSOCIATED WITH",           color: "#A39E93" },
  employed_by:                       { label: "EMPLOYED BY",               color: "#9FA8D4" },
  represented_by:                    { label: "REPRESENTED BY",            color: "#CBB892" },
  testified_against:                 { label: "TESTIFIED AGAINST",         color: "#D92D20" },
  accused_by:                        { label: "ACCUSED BY",                color: "#D92D20" },
  visited:                           { label: "VISITED",                   color: "#5DD4B3" },
  traveled_with:                     { label: "TRAVELED WITH",             color: "#9FA8D4" },
  appears_in_document:               { label: "APPEARS IN DOCUMENT",       color: "#5DD4B3" },
  owned:                             { label: "OWNED",                     color: "#E0A526" },
  controlled:                        { label: "CONTROLLED",                color: "#E0A526" },
  resided_at:                        { label: "RESIDED AT",                color: "#5DD4B3" },
  operated_from:                     { label: "OPERATED FROM",             color: "#5DD4B3" },
  tied_to_event:                     { label: "TIED TO EVENT",             color: "#CBB892" },
  owns:                              { label: "OWNS",                      color: "#E0A526" },
  controls:                          { label: "CONTROLS",                  color: "#E0A526" },
  directs:                           { label: "DIRECTS",                   color: "#E0A526" },
  linked_to:                         { label: "LINKED TO",                 color: "#A39E93" },
  transferred_to:                    { label: "TRANSFERRED TO",            color: "#E0A526" },
  paid:                              { label: "PAID",                      color: "#E0A526" },
  received_payment_from:             { label: "RECEIVED PAYMENT FROM",     color: "#E0A526" },
  funded:                            { label: "FUNDED",                    color: "#E0A526" },
  settled_with:                      { label: "SETTLED WITH",              color: "#D92D20" },
  managed_by:                        { label: "MANAGED BY",                color: "#9FA8D4" },
  registered_to:                     { label: "REGISTERED TO",             color: "#A39E93" },
  beneficiary_of:                    { label: "BENEFICIARY OF",            color: "#E0A526" },
  flew_on:                           { label: "FLEW ON",                   color: "#9FA8D4" },
  operated_aircraft:                 { label: "OPERATED AIRCRAFT",         color: "#9FA8D4" },
  passenger_on:                      { label: "PASSENGER ON",              color: "#9FA8D4" },
  arrived_at:                        { label: "ARRIVED AT",                color: "#5DD4B3" },
  departed_from:                     { label: "DEPARTED FROM",             color: "#5DD4B3" },
  manifest_lists:                    { label: "APPEARS IN MANIFEST",       color: "#9FA8D4" },
  route_to:                          { label: "ROUTE TO",                  color: "#9FA8D4" },
  tied_to_aircraft:                  { label: "TIED TO AIRCRAFT",          color: "#9FA8D4" },
  documented_in:                     { label: "DOCUMENTED IN",             color: "#5DD4B3" },
  sourced_by:                        { label: "SOURCED BY",                color: "#5DD4B3" },
  filed_against:                     { label: "FILED AGAINST",             color: "#D92D20" },
  references:                        { label: "REFERENCES",                color: "#A39E93" },
  corroborates:                      { label: "CORROBORATES",              color: "#5DD4B3" },
  disputes:                          { label: "DISPUTES",                  color: "#E0A526" },
  connected_to_case:                 { label: "CONNECTED TO CASE",         color: "#D92D20" },
};

export const CONFIDENCE_META = {
  high:   { label: "HIGH",   color: "#5DD4B3" },
  medium: { label: "MEDIUM", color: "#CBB892" },
  low:    { label: "LOW",    color: "#E0A526" },
};

export const FINANCIAL_REL_TYPES = new Set([
  // Legacy lane (Batch 1 / 2)
  "financial_link", "business_association", "property_association",
  // Phase 2 expansion
  "ownership_link", "trust_link", "company_link",
  "owns", "owned", "controls", "controlled", "directs",
  "transferred_to", "paid", "received_payment_from", "funded",
  "settled_with", "managed_by", "registered_to", "beneficiary_of",
  "linked_to",
]);

// Entity kinds that belong in Follow the Money even when they have no edge yet.
export const FINANCIAL_ENTITY_KINDS = new Set([
  "financial", "financial_entity", "shell_company", "trust",
  "company", "account",
]);

// Entity kinds that belong in Flights / Travel lane.
export const TRAVEL_ENTITY_KINDS = new Set([
  "flight", "aircraft", "flight_event", "vessel",
]);

// Entity kinds that belong in Court / Legal lane.
export const LEGAL_ENTITY_KINDS = new Set([
  "filing", "legal_record",
]);

// Filter chip definitions — V2 board filters (order matches the noir dashboard spec)
export const BOARD_FILTERS = [
  { key: "all",       label: "All",            test: () => true },
  { key: "financial", label: "Financial / Money", test: (e) => ["financial_link","business_association","property_association","ownership_link","trust_link","company_link","owns","controls","directs","linked_to","transferred_to","paid","received_payment_from","funded","settled_with","managed_by","registered_to","beneficiary_of"].includes(e.type) || ["financial","financial_entity","shell_company","trust","company","account"].includes(e.kind) },
  { key: "flights",   label: "Flights / Travel", test: (e) => ["named_in_flight_log","appears_in_same_flight_log","listed_in_flight_logs","travel_association","flew_on","operated_aircraft","passenger_on","arrived_at","departed_from","manifest_lists","route_to","tied_to_aircraft","traveled_with"].includes(e.type) || ["flight","aircraft","flight_event","vessel"].includes(e.kind) },
  { key: "legal",     label: "Court / Legal",  test: (e) => ["named_in_court_filing","named_in_deposition","legal_association","settlement_reference","document_reference","filed_against","connected_to_case","testified_against","accused_by"].includes(e.type) || ["filing","legal_record"].includes(e.kind) },
  { key: "property",  label: "Properties",     test: (e) => ["property_association","owned","controlled","resided_at","operated_from","visited","tied_to_event"].includes(e.type) || ["property","location"].includes(e.kind) },
  { key: "photos",    label: "Photos / Media", test: (e) => ["photographed_with"].includes(e.type) || e.kind === "photo" },
  { key: "emails",    label: "Emails / Comms", test: (e) => ["email_contact","communication_or_contact_reference"].includes(e.type) || e.kind === "email" },
  { key: "gov",       label: "Gov Records",    test: (e) => ["government_record_reference"].includes(e.type) || e.kind === "government_record" },
  { key: "witness",   label: "Witness",        test: (e) => ["witness_claim_association","witness_reference","victim_allegation_reference","accused_by"].includes(e.type) },
  { key: "lowconf",   label: "Low Confidence", test: (e) => e.confidence === "low" || e.confidence_level === "low" },
];

// Legal label mapping — derives a public-facing label from evidence_type/confidence
export function deriveLegalLabel(edgeOrEntity) {
  const et = edgeOrEntity.evidence_type;
  const t = edgeOrEntity.type; // relationship type
  if (t === "named_in_court_filing" || et === "court_filing") return { label: "COURT FILING MENTION", color: "#D92D20" };
  if (t === "named_in_deposition" || et === "deposition") return { label: "DEPOSITION MENTION", color: "#D92D20" };
  if (t === "named_in_flight_log" || t === "listed_in_flight_logs" || t === "appears_in_same_flight_log" || et === "flight_log") return { label: "FLIGHT LOG MENTION", color: "#9FA8D4" };
  if (t === "photographed_with" || et === "photo") return { label: "PHOTO RECORD", color: "#CBB892" };
  if (et === "email" || t === "email_contact") return { label: "EMAIL RECORD", color: "#9FA8D4" };
  if (t === "witness_claim_association" || t === "victim_allegation_reference" || et === "witness_claim") return { label: "ALLEGATION", color: "#E0A526" };
  if (et === "government_record" || et === "foia_document" || et === "declassified_document") return { label: "GOVERNMENT RECORD", color: "#5DD4B3" };
  if (et === "media_report") return { label: "MEDIA REPORT", color: "#A39E93" };
  if (edgeOrEntity.confidence === "low") return { label: "LOW CONFIDENCE", color: "#E0A526" };
  return null;
}

// Evidence-type grouping for the Document Gallery
export const EVIDENCE_GROUPS = [
  { key: "court_filing",       label: "COURT FILINGS" },
  { key: "deposition",         label: "DEPOSITIONS" },
  { key: "exhibit",            label: "EXHIBITS" },
  { key: "flight_log",         label: "FLIGHT LOGS" },
  { key: "email",              label: "EMAILS" },
  { key: "photo",              label: "PHOTOS" },
  { key: "financial_record",   label: "FINANCIAL RECORDS" },
  { key: "property_record",    label: "PROPERTY RECORDS" },
  { key: "government_record",  label: "GOV / FOIA RECORDS" },
  { key: "foia_document",      label: "FOIA" },
  { key: "media_report",       label: "MEDIA REPORTS" },
  { key: "witness_claim",      label: "WITNESS CLAIMS" },
  { key: "other",              label: "OTHER" },
];

// Public directory categories — used in the "By Category" mode
export const DIRECTORY_CATEGORIES = [
  "Anchor", "Financiers", "Business Partners", "Victims / Survivors",
  "Recruiters / Facilitators", "Associates",
  "Government / Legal Records", "Court Filings / Legal",
  "Financial / Money Trail", "Flight Logs / Travel",
  "Properties / Locations", "Emails / Communications",
  "Photos / Media", "Timeline Events",
  "Organizations / Companies", "Witnesses / Testimony",
  // Phase 2 mixed-node buckets — Directory By Kind falls back to these when
  // an entity has no `category_label` set.
  "Shell Companies / Trusts", "Aircraft / Flight Events", "Documents / Records",
  "Other",
];

// Bucket label by entity kind — used as a fallback when no category_label is set
export const KIND_TO_DIRECTORY_BUCKET = {
  person: "Associates",
  organization: "Organizations / Companies",
  government_office: "Government / Legal Records",
  company: "Organizations / Companies",
  property: "Properties / Locations",
  location: "Properties / Locations",
  financial: "Financial / Money Trail",
  financial_entity: "Shell Companies / Trusts",
  shell_company: "Shell Companies / Trusts",
  trust: "Shell Companies / Trusts",
  account: "Shell Companies / Trusts",
  aircraft: "Aircraft / Flight Events",
  flight: "Aircraft / Flight Events",
  flight_event: "Aircraft / Flight Events",
  vessel: "Aircraft / Flight Events",
  document: "Documents / Records",
  email: "Emails / Communications",
  photo: "Photos / Media",
  filing: "Court Filings / Legal",
  legal_record: "Court Filings / Legal",
  government_record: "Government / Legal Records",
  event: "Timeline Events",
  timeline_event: "Timeline Events",
  reference: "Other",
  phone_number: "Other",
  alias: "Other",
};

// Public disclaimer string used across V1.3 surfaces
export const EF_DISCLAIMER =
  "Appearance in records, photos, logs, filings, emails, or testimony does not imply guilt or criminal conduct. Connections are displayed only where supported by approved sources.";

/**
 * Return up to ~3 short "kind-specific" facts for a node card.
 * Pure data — no JSX. The caller decides how to render. Used by the Evidence
 * Board card, Inline detail panel, and Mobile bottom sheet to give mixed-kind
 * nodes (aircraft, flight events, shell companies, legal records, properties,
 * documents) a distinct identity without breaking the dossier aesthetic.
 *
 * Returns: Array<{label: string, value: string}>
 */
export function kindMetaBadges(entity = {}) {
  if (!entity || typeof entity !== "object") return [];
  const out = [];
  const push = (label, value) => {
    if (value === undefined || value === null) return;
    const v = typeof value === "string" ? value.trim() : String(value);
    if (!v) return;
    out.push({ label, value: v });
  };
  switch (entity.kind) {
    case "aircraft":
    case "flight":
      push("TAIL", entity.tail_number || entity.registration);
      push("MODEL", entity.aircraft_model || entity.model);
      push("OPERATOR", entity.operator_label || entity.operator);
      break;
    case "flight_event":
      push("DATE", entity.flight_date_start || entity.date_label || entity.date);
      push("ROUTE", entity.route_label ||
        ((entity.origin_label && entity.destination_label)
          ? `${entity.origin_label} → ${entity.destination_label}`
          : entity.origin || entity.destination
            ? `${entity.origin || "?"} → ${entity.destination || "?"}`
            : null));
      push("MANIFEST", entity.manifest_reference || entity.manifest_label);
      break;
    case "financial_entity":
    case "shell_company":
    case "trust":
    case "account":
      push("TYPE", entity.financial_type || entity.subtype);
      push("JURISDICTION", entity.jurisdiction);
      push("INCORP", entity.incorporation_location);
      break;
    case "company":
    case "organization":
      push("TYPE", entity.org_type || entity.subtype);
      push("JURISDICTION", entity.jurisdiction || entity.nationality);
      break;
    case "property":
    case "location":
      push("TYPE", entity.property_type || entity.subtype);
      push("LOCATION", entity.location_label || entity.location);
      push("COUNTRY", entity.country);
      break;
    case "legal_record":
    case "filing":
      push("VENUE", entity.court_or_venue || entity.court);
      push("FILED", entity.filing_date || entity.date);
      push("RESOLVED", entity.resolution_date);
      break;
    case "document":
      push("TYPE", entity.document_type || entity.subtype);
      push("DATE", entity.document_date || entity.date);
      push("ORIGIN", entity.origin_source_label || entity.origin_label || entity.origin);
      break;
    case "timeline_event":
    case "event":
      push("DATE", entity.event_date || entity.date);
      push("RANGE",
        (entity.event_start && entity.event_end)
          ? `${entity.event_start} → ${entity.event_end}`
          : null);
      break;
    default:
      if (entity.occupation_or_role && entity.kind !== "person") {
        push("ROLE", entity.occupation_or_role);
      }
  }
  return out.slice(0, 3);
}
